/**
 * Выбор программ обучения деревом разделов каталога (training-program-picker; ТЗ 26.10.2026,
 * п.4.1–4.3, 4.6): плашек под поиском нет, разделы — плашками-кнопками на всю ширину, изначально
 * всё свёрнуто, у раздела — «N программ» и шеврон, у раздела с выбранными — «выбрано K».
 * Подразделы — с отступом, до трёх уровней, в порядке каталога. Поиск (от 2 символов) — по
 * краткому и полному наименованию, коду и «Подлежат обучению»; раскрывает разделы с совпадениями
 * и подсвечивает их. «Только выбранные» — оставлено.
 *
 * Что делать с программой (выбрать сотрудников, отметить), решает вызывающий: `renderActions`,
 * `renderDetails` и `selectedCount`. Неподтверждённые и архивные программы вызывающий в
 * `programs` не передаёт (кроме уже выбранных архивных — их видно с пометкой).
 *
 * Пакеты программ (training-order-packages; ТЗ 4.4.2, Лист2 каталога) — первым разделом
 * «Популярные пакеты программ»: строка пакета — название и его программы строками программ.
 * Поиск находит пакет по названию и по его программам; «Только выбранные» — выбранные пакеты.
 *
 * Ответ заказчицы 04.11 к п.4.1–4.3:
 * - три цвета корневых плашек: «Популярные пакеты»; «Охрана труда», «Пожарная безопасность»,
 *   «Промбез», «Экология»; остальные. У подразделов — рамка цвета родителя;
 * - плашки уже: одной ширины в пределах уровня — по самому длинному названию, с местом под
 *   «выбрано K», число программ и кнопку «Развернуть / Свернуть»; «Свернуть все» — справа в строке
 *   «Только выбранные»;
 * - рядом с поиском — фильтры по выдаваемому документу, числу часов и периодичности.
 */
import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react'
import { ChevronDown, SearchX } from 'lucide-react'
import type { Program, TrainingDirectionItem } from '@/lib/api'
import {
  DOCUMENT_KIND_LABELS,
  documentKind,
  flattenDirections,
  formatHours,
  periodFilterKey,
  programMatches,
  searchKey,
  type DirectionNode,
  type DocumentKind,
} from '@/lib/training-catalog'
import { cn } from '@/lib/utils'
import { Input } from '@/design-system/components/primitives/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'
import { Badge } from '@/design-system/components/primitives/badge'
import { Button } from '@/design-system/components/primitives/button'
import { EmptyState } from '@/design-system/components/feedback/empty-state'
import { LabeledCheckbox } from '@/sout/labeled-checkbox'
import { Highlight, ProgramTitle } from './program-title'

/** «1 программа», «3 программы», «12 программ». */
export function programsCount(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  let word = 'программ'
  if (mod10 === 1 && mod100 !== 11) word = 'программа'
  else if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) word = 'программы'
  return `${n} ${word}`
}

/** «1 пакет», «3 пакета», «5 пакетов». */
export function packagesCount(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  let word = 'пакетов'
  if (mod10 === 1 && mod100 !== 11) word = 'пакет'
  else if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) word = 'пакета'
  return `${n} ${word}`
}

/** Минимальная длина поиска (короче — показывается всё дерево). */
const SEARCH_MIN = 2

/** Пакет программ в выборе: название и программы в порядке пакета. */
export interface PickerPackage {
  id: string
  name: string
  programs: Program[]
}

/** Раздел пакетов — первым, как в дереве каталога заказчицы (Лист2). */
export const PACKAGES_SECTION_ID = '__packages__'
const PACKAGES_SECTION_NAME = 'Популярные пакеты программ'

/** Поле поиска каталога — к нему ведёт кнопка «Поиск программ» мастера. */
export const PROGRAM_SEARCH_INPUT_ID = 'program-tree-search'

/** Поставить фокус в поиск каталога и прокрутить к нему (кнопка «Поиск программ»). */
export function focusProgramSearch(): void {
  const input = document.getElementById(PROGRAM_SEARCH_INPUT_ID)
  if (!(input instanceof HTMLInputElement)) return
  input.scrollIntoView?.({ block: 'center' })
  input.focus({ preventScroll: true })
}

/** Цветовая группа корневого раздела (ответ заказчицы 04.11). */
export type SectionTone = 'popular' | 'core' | 'other'

/**
 * Основные разделы — по названию корня каталога (Лист2): «Охрана труда», «Пожарная безопасность»,
 * «Промбез» (и полное «Промышленная безопасность»), «Экология». Переименованный администратором
 * раздел уходит в «остальные» — цвет лишь оформление, на выбор программ не влияет.
 */
const CORE_SECTIONS = new Set(
  ['Охрана труда', 'Пожарная безопасность', 'Промбез', 'Промышленная безопасность', 'Экология'].map(
    searchKey,
  ),
)

export function sectionTone(rootName: string): SectionTone {
  return CORE_SECTIONS.has(searchKey(rootName.trim())) ? 'core' : 'other'
}

/** Рамка и фон плашки раздела: корень — заливка цвета группы, подраздел — только рамка. */
const TONE_ROOT: Record<SectionTone, string> = {
  popular: 'border-cat-popular bg-cat-popular-bg',
  core: 'border-cat-core bg-cat-core-bg',
  other: 'border-cat-other bg-cat-other-bg',
}
const TONE_NESTED: Record<SectionTone, string> = {
  popular: 'border-cat-popular bg-panel',
  core: 'border-cat-core bg-panel',
  other: 'border-cat-other bg-panel',
}

/** «Все» в фильтрах каталога. */
const ANY = '__any__'

/** Значения фильтров каталога, которые есть у переданных программ (пустых вариантов нет). */
function filterOptions(programs: Program[]) {
  const kinds = new Set<DocumentKind>()
  const hours = new Set<number>()
  const periods = new Map<string, { label: string; order: number }>()
  for (const p of programs) {
    const kind = documentKind(p.documentType)
    if (kind) kinds.add(kind)
    if (p.hours != null && p.hours > 0) hours.add(p.hours)
    const period = periodFilterKey(p)
    periods.set(period.key, { label: period.label, order: period.order })
  }
  return {
    kinds: (Object.keys(DOCUMENT_KIND_LABELS) as DocumentKind[]).filter((k) => kinds.has(k)),
    hours: [...hours].sort((a, b) => a - b),
    periods: [...periods.entries()]
      .sort(([, a], [, b]) => a.order - b.order || a.label.localeCompare(b.label, 'ru'))
      .map(([key, v]) => ({ key, label: v.label })),
  }
}

interface Section {
  node: DirectionNode
  /** Найденные программы самого раздела (без подразделов), в порядке каталога. */
  programs: Program[]
  children: Section[]
  /** Найдено в разделе вместе с подразделами. */
  total: number
  /** Выбрано в разделе вместе с подразделами. */
  selected: number
}

export function ProgramTreePicker<T extends Program, K extends PickerPackage = PickerPackage>({
  programs,
  directions,
  selectedCount,
  renderActions,
  renderDetails,
  showPrice = false,
  packages = [],
  packageSelectedCount = () => 0,
  renderPackageActions,
}: {
  programs: T[]
  directions: TrainingDirectionItem[]
  /** Сколько выбрано по программе (0 — не выбрана): «выбрано K» у раздела, «Только выбранные». */
  selectedCount: (p: T) => number
  renderActions?: (p: T) => ReactNode
  /** Под строкой программы — например, выбранные сотрудники или пометка «больше нет в каталоге». */
  renderDetails?: (p: T) => ReactNode
  /** Цена справа бледно — только команде. */
  showPrice?: boolean
  /** Пакеты программ — раздел «Популярные пакеты программ» первым (пусто — раздела нет). */
  packages?: K[]
  /** Сколько выбрано по пакету (0 — не выбран). */
  packageSelectedCount?: (p: K) => number
  renderPackageActions?: (p: K) => ReactNode
}) {
  const [query, setQuery] = useState('')
  const [onlySelected, setOnlySelected] = useState(false)
  const [docFilter, setDocFilter] = useState(ANY)
  const [hoursFilter, setHoursFilter] = useState(ANY)
  const [periodFilter, setPeriodFilter] = useState(ANY)
  const [open, setOpen] = useState<Set<string>>(() => new Set())

  const searching = searchKey(query.trim()).length >= SEARCH_MIN
  const attrFilter = docFilter !== ANY || hoursFilter !== ANY || periodFilter !== ANY
  const filterActive = searching || onlySelected || attrFilter

  const nodes = useMemo(() => flattenDirections(directions), [directions])
  const options = useMemo(() => filterOptions(programs), [programs])

  /** Программа подходит под фильтры документа, часов и периодичности. */
  const matchesAttrs = (p: Program): boolean =>
    (docFilter === ANY || documentKind(p.documentType) === docFilter) &&
    (hoursFilter === ANY || String(p.hours ?? '') === hoursFilter) &&
    (periodFilter === ANY || periodFilterKey(p).key === periodFilter)

  // Дерево разделов с найденными программами. Раздел без найденных программ (сам и в
  // подразделах) не показывается.
  const { roots, found } = useMemo(() => {
    const byDirection = new Map<string, T[]>()
    let found = 0
    for (const p of programs) {
      if (searching && !programMatches(p, query)) continue
      if (onlySelected && selectedCount(p) === 0) continue
      if (!matchesAttrs(p)) continue
      const list = byDirection.get(p.directionId) ?? []
      list.push(p)
      byDirection.set(p.directionId, list)
      found++
    }
    const sections = new Map<string, Section>()
    for (const node of nodes) {
      sections.set(node.direction.id, {
        node,
        programs: byDirection.get(node.direction.id) ?? [],
        children: [],
        total: 0,
        selected: 0,
      })
    }
    // Программы раздела, которого нет в дереве (не должно быть), — отдельным корнем в конце.
    const orphans: T[] = []
    for (const [dirId, list] of byDirection) if (!sections.has(dirId)) orphans.push(...list)
    const roots: Section[] = []
    for (const node of nodes) {
      const s = sections.get(node.direction.id) as Section
      const parent = node.direction.parentId ? sections.get(node.direction.parentId) : undefined
      if (parent && node.depth > 1) parent.children.push(s)
      else roots.push(s)
    }
    if (orphans.length > 0) {
      roots.push({
        node: {
          direction: {
            id: '__other__',
            name: 'Прочие программы',
            parentId: '',
            sort: 0,
            systemCode: '',
            system: false,
          },
          depth: 1,
          path: ['Прочие программы'],
        },
        programs: orphans,
        children: [],
        total: 0,
        selected: 0,
      })
    }
    const count = (s: Section): void => {
      s.children.forEach(count)
      s.children = s.children.filter((c) => c.total > 0)
      s.total = s.programs.length + s.children.reduce((n, c) => n + c.total, 0)
      s.selected =
        s.programs.filter((p) => selectedCount(p as T) > 0).length +
        s.children.reduce((n, c) => n + c.selected, 0)
    }
    roots.forEach(count)
    return { roots: roots.filter((r) => r.total > 0), found }
    // selectedCount — функция вызывающего; выбор меняется вместе с programs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programs, nodes, query, searching, onlySelected, docFilter, hoursFilter, periodFilter])

  // Пакеты, подходящие под поиск (по названию пакета или его программам) и «Только выбранные».
  const foundPackages = useMemo(
    () =>
      packages.filter(
        (pk) =>
          (!searching ||
            searchKey(pk.name).includes(searchKey(query.trim())) ||
            pk.programs.some((p) => programMatches(p, query))) &&
          (!onlySelected || packageSelectedCount(pk) > 0) &&
          // Фильтры документа, часов и периодичности — пакет, где есть подходящая программа.
          (!attrFilter || pk.programs.some(matchesAttrs)),
      ),
    // packageSelectedCount — функция вызывающего; выбор меняется вместе с packages.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [packages, query, searching, onlySelected, docFilter, hoursFilter, periodFilter],
  )
  const selectedPackages = foundPackages.filter((pk) => packageSelectedCount(pk) > 0).length
  const sectionsCount = roots.length + (foundPackages.length > 0 ? 1 : 0)

  // Изначально всё свёрнуто (ТЗ 4.2) — кроме единственного раздела: сворачивать нечего. Поиск и
  // «Только выбранные» раскрывают все разделы с найденным; сброс — снова всё свёрнуто. Ручное
  // раскрытие сохраняется до смены условий.
  const singleRoot =
    sectionsCount !== 1 ? '' : roots.length === 1 ? roots[0].node.direction.id : PACKAGES_SECTION_ID
  useEffect(() => {
    if (!filterActive) {
      setOpen(new Set(singleRoot ? [singleRoot] : []))
      return
    }
    const ids = new Set<string>()
    const walk = (s: Section) => {
      ids.add(s.node.direction.id)
      s.children.forEach(walk)
    }
    roots.forEach(walk)
    if (foundPackages.length > 0) ids.add(PACKAGES_SECTION_ID)
    setOpen(ids)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, onlySelected, docFilter, hoursFilter, periodFilter, singleRoot])

  function toggle(id: string) {
    setOpen((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function reset() {
    setQuery('')
    setOnlySelected(false)
    setDocFilter(ANY)
    setHoursFilter(ANY)
    setPeriodFilter(ANY)
  }

  function collapseAll() {
    setOpen(new Set())
  }

  function renderProgram(p: T) {
    const actions = renderActions?.(p)
    const details = renderDetails?.(p)
    return (
      <li key={p.id} className="flex flex-wrap items-start gap-x-3 gap-y-2 p-3 nw:px-4">
        <div className="min-w-0 flex-1 basis-64">
          <ProgramTitle program={p} query={searching ? query : undefined} showPrice={showPrice} />
          {details}
        </div>
        {actions ? (
          <div className="ml-auto flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
        ) : null}
      </li>
    )
  }

  function renderPackage(pk: K) {
    const actions = renderPackageActions?.(pk)
    return (
      <li key={pk.id} className="flex flex-wrap items-start gap-x-3 gap-y-2 p-3 nw:px-4">
        <div className="min-w-0 flex-1 basis-64">
          <div className="break-words font-semibold text-txt">
            <Highlight text={pk.name} query={searching ? query : undefined} />
          </div>
          <ul
            className="mt-2 flex flex-col gap-2 border-l-2 border-line pl-3"
            aria-label={`Программы пакета «${pk.name}»`}
          >
            {pk.programs.map((p) => (
              <li key={p.id}>
                <ProgramTitle
                  program={p}
                  query={searching ? query : undefined}
                  showPrice={showPrice}
                  inPackage
                />
              </li>
            ))}
          </ul>
        </div>
        {actions ? (
          <div className="ml-auto flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
        ) : null}
      </li>
    )
  }

  /**
   * Плашка раздела: название, «выбрано K», «N программ», «Развернуть / Свернуть»; ниже — раскрытое
   * содержимое на всю ширину. Плашка и содержимое — две ячейки сетки уровня (`SectionLevel`): все
   * плашки уровня одной ширины — по самой длинной. Места под «выбрано K» и число программ
   * зарезервированы, чтобы ширина не прыгала при выборе.
   */
  function sectionHeader(
    id: string,
    name: string,
    nested: boolean,
    tone: SectionTone,
    selected: number,
    countLabel: string,
    body: ReactNode,
  ) {
    const isOpen = open.has(id)
    const bodyId = `program-section-${id}`
    return (
      <Fragment key={id}>
        <button
          type="button"
          onClick={() => toggle(id)}
          aria-expanded={isOpen}
          aria-controls={bodyId}
          className={cn(
            'ds-focus-ring flex w-full items-center gap-3 rounded-10 px-3 py-2 text-left transition-colors hover:brightness-95 nw:col-start-1 nw:px-4',
            nested ? cn('border', TONE_NESTED[tone]) : cn('border-2', TONE_ROOT[tone]),
          )}
        >
          <span
            className={cn(
              'min-w-0 flex-1 break-words text-txt',
              nested ? 'text-14' : 'text-15 font-semibold',
            )}
          >
            {name}
          </span>
          <span className="flex min-w-24 shrink-0 justify-end">
            {selected > 0 ? <Badge status="info">выбрано {selected}</Badge> : null}
          </span>
          <span className="min-w-24 shrink-0 whitespace-nowrap text-right text-12 text-txt-tertiary">
            {countLabel}
          </span>
          <span className="flex min-w-24 shrink-0 items-center justify-end gap-1 text-12 text-txt-secondary">
            {isOpen ? 'Свернуть' : 'Развернуть'}
            <ChevronDown
              aria-hidden
              className={cn(
                'size-4 shrink-0 stroke-current transition-transform',
                isOpen && 'rotate-180',
              )}
            />
          </span>
        </button>
        {isOpen ? (
          <div id={bodyId} className="flex flex-col gap-2 nw:col-span-2">
            {body}
          </div>
        ) : null}
      </Fragment>
    )
  }

  function renderPackagesSection() {
    if (foundPackages.length === 0) return null
    return sectionHeader(
      PACKAGES_SECTION_ID,
      PACKAGES_SECTION_NAME,
      false,
      'popular',
      selectedPackages,
      packagesCount(foundPackages.length),
      <ul
        className="divide-y divide-line rounded-10 border border-line bg-panel"
        aria-label={`Пакеты раздела «${PACKAGES_SECTION_NAME}»`}
      >
        {foundPackages.map(renderPackage)}
      </ul>,
    )
  }

  function renderSection(s: Section, tone: SectionTone): ReactNode {
    return sectionHeader(
      s.node.direction.id,
      s.node.direction.name,
      s.node.depth > 1,
      tone,
      s.selected,
      programsCount(s.total),
      <>
        {s.programs.length > 0 ? (
          <ul
            className="divide-y divide-line rounded-10 border border-line bg-panel"
            aria-label={`Программы раздела «${s.node.direction.name}»`}
          >
            {s.programs.map((p) => renderProgram(p as T))}
          </ul>
        ) : null}
        {s.children.length > 0 ? (
          <SectionLevel nested>{s.children.map((c) => renderSection(c, tone))}</SectionLevel>
        ) : null}
      </>,
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap gap-2">
          <div className="min-w-64 flex-1">
            <Input
              id={PROGRAM_SEARCH_INPUT_ID}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Название, код или кто подлежит обучению"
              aria-label="Поиск программы"
            />
          </div>
          {options.kinds.length > 1 ? (
            <Select value={docFilter} onValueChange={setDocFilter}>
              <SelectTrigger className="w-44" aria-label="Выдаваемый документ">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Любой документ</SelectItem>
                {options.kinds.map((k) => (
                  <SelectItem key={k} value={k}>
                    {DOCUMENT_KIND_LABELS[k]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
          {options.hours.length > 1 ? (
            <Select value={hoursFilter} onValueChange={setHoursFilter}>
              <SelectTrigger className="w-36" aria-label="Количество часов">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Любые часы</SelectItem>
                {options.hours.map((h) => (
                  <SelectItem key={h} value={String(h)}>
                    {formatHours(h)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
          {options.periods.length > 1 ? (
            <Select value={periodFilter} onValueChange={setPeriodFilter}>
              <SelectTrigger className="w-60" aria-label="Периодичность">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Любая периодичность</SelectItem>
                {options.periods.map((o) => (
                  <SelectItem key={o.key} value={o.key}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <LabeledCheckbox checked={onlySelected} onCheckedChange={setOnlySelected}>
            Только выбранные
          </LabeledCheckbox>
          <div className="flex flex-wrap items-center gap-3">
            {filterActive ? (
              <>
                <span className="text-13 text-txt-secondary" aria-live="polite">
                  Найдено:{' '}
                  {foundPackages.length > 0
                    ? `${programsCount(found)}, ${packagesCount(foundPackages.length)}`
                    : found}
                </span>
                <Button type="button" size="sm" variant="secondary" onClick={reset}>
                  Сбросить
                </Button>
              </>
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={open.size === 0}
              onClick={collapseAll}
            >
              Свернуть все
            </Button>
          </div>
        </div>
      </div>
      {sectionsCount === 0 ? (
        <EmptyState
          icon={SearchX}
          title={onlySelected && !searching ? 'Программы ещё не выбраны' : 'Ничего не найдено'}
          description={
            searching
              ? 'Измените запрос — ищем по названию, коду и должностям, кто подлежит обучению'
              : undefined
          }
          action={
            <Button size="sm" variant="secondary" onClick={reset}>
              Сбросить
            </Button>
          }
        />
      ) : (
        <SectionLevel>
          {renderPackagesSection()}
          {roots.map((r) => renderSection(r, sectionTone(r.node.direction.name)))}
        </SectionLevel>
      )}
    </div>
  )
}

/**
 * Уровень дерева: плашки разделов в первой колонке сетки (одной ширины — по самой длинной),
 * раскрытое содержимое — на всю ширину. На узком экране — столбцом во всю ширину.
 */
function SectionLevel({ nested = false, children }: { nested?: boolean; children: ReactNode }) {
  return (
    <div
      className={cn('flex flex-col gap-2 nw:grid nw:grid-cols-fit-rest', nested && 'ml-3 nw:ml-5')}
    >
      {children}
    </div>
  )
}
